import React, { useCallback, useEffect, useState } from "react";

import styles from "./actionQueue.module.css";
import ActionDetailsModal from "./actionDetailsModal";
import DismissConfirmModal, { shouldSkipDismissConfirm } from "./dismissConfirmModal";
import Button from "../button/button";
import UpdateIcon from "../../assets/icons/update-icon.svg";
import { getActionItems, getActionOutcomes, runActionItems, updateActionItemStatus } from "../../services/actionItems";
import { formatNumberToCurrency, formatShortDate, pluralize } from "../../utils/formatter";

const PRIORITY_LABEL = { 3: "urgente", 2: "normal", 1: "baixa" };
const OUTCOME_DAYS = 90;

const parseJson = (text) => {
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    return {};
  }
};

const parseEvidence = (item) => parseJson(item?.evidenceJson);

// O que a ação já rendeu enquanto está aberta (OutcomeJson, medido pelo job).
const outcomeSummary = (item) => {
  const outcome = parseJson(item?.outcomeJson);
  if (outcome.recoveredValue > 0) {
    return `${formatNumberToCurrency(Number(outcome.recoveredValue))} já recebidos`;
  }
  if (outcome.reenrolledStudents > 0) {
    return `${pluralize(outcome.reenrolledStudents, "aluno já rematriculado", "alunos já rematriculados")}`;
  }
  return null;
};

const evidenceSummary = (item, evidence) => {
  if (item.type === "EVASAO_RISCO_ALTO") {
    const parts = [];
    if (evidence.course) {
      parts.push(evidence.course);
    }
    parts.push(`score ${evidence.score ?? "-"}`);
    if (evidence.dropoutRate != null) {
      parts.push(`${Math.round(evidence.dropoutRate * 100)}% de chance de evadir`);
    }
    if (evidence.avgFrequency != null) {
      parts.push(`freq. ${Number(evidence.avgFrequency).toFixed(0)}%`);
    }
    if (evidence.avgFinalGrade != null) {
      parts.push(`nota ${Number(evidence.avgFinalGrade).toFixed(1)}`);
    }
    if (evidence.overdueInstallments > 0) {
      const plural = evidence.overdueInstallments !== 1 ? "s" : "";
      parts.push(`${evidence.overdueInstallments} parcela${plural} vencida${plural}`);
    }
    return parts.join(" · ");
  }

  if (item.type === "DADOS_DEFASADOS") {
    const parts = [`última importação em ${formatShortDate(evidence.lastImportAt)}`];
    if (evidence.paymentCoverage) {
      parts.push(`último pagamento registrado em ${formatShortDate(evidence.paymentCoverage)}`);
    }
    return parts.join(" · ");
  }

  const parts = [];
  if (evidence.count != null) {
    parts.push(`${evidence.count} aluno${evidence.count !== 1 ? "s" : ""}`);
  }
  if (evidence.total != null) {
    parts.push(formatNumberToCurrency(Number(evidence.total)));
  }
  return parts.join(" · ");
};

const ActionQueue = ({ institutionId }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  // { item, evidence } da ação aberta no modal de detalhes
  const [selected, setSelected] = useState(null);
  // ação aguardando confirmação de "Ignorar"
  const [dismissing, setDismissing] = useState(null);

  const load = useCallback(async () => {
    if (!institutionId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Resultado é complementar: se falhar, a fila continua.
      const [data, outcomeData] = await Promise.all([
        getActionItems(institutionId),
        getActionOutcomes(institutionId, OUTCOME_DAYS).catch(() => null)
      ]);
      setItems(data?.actionItems || []);
      setSummary(data?.summary || null);
      setOutcomes(outcomeData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async () => {
    setRunning(true);
    try {
      await runActionItems(institutionId);
      await load();
    } catch (err) {
      setError("Não foi possível gerar as ações agora.");
    } finally {
      setRunning(false);
    }
  };

  // A ação sai da fila sozinha quando o job deixa de detectar a condição (boleto pago,
  // aluno fora do risco). O gestor só intervém para descartar o que não se aplica.
  const handleDismiss = (item) => {
    if (shouldSkipDismissConfirm()) {
      dismissItem(item);
      return;
    }
    setDismissing(item);
  };

  const dismissItem = async (item) => {
    setDismissing(null);
    try {
      await updateActionItemStatus(institutionId, item.id, "dismissed");
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setSummary((prev) => (prev ? {
        ...prev,
        count: Math.max(0, (prev.count || 1) - 1),
        pendingTotal: Math.max(0, (prev.pendingTotal || 1) - 1),
        totalValue: Math.max(0, (prev.totalValue || 0) - (item.estimatedValue || 0))
      } : prev));
    } catch (err) {
      setError("Não foi possível descartar a ação.");
    }
  };

  // Tiles só quando há o que mostrar; medido mas zerado vira uma linha discreta.
  const hasResults = Boolean(outcomes)
    && (outcomes.recoveredValue > 0 || outcomes.retainedStudents > 0 || outcomes.reenrolledStudents > 0);

  const subtitleParts = [];
  if (summary) {
    subtitleParts.push(`${summary.count} ${summary.count === 1 ? "ação" : "ações"} para hoje`);

    if (summary.totalValue > 0) {
      subtitleParts.push(`${formatNumberToCurrency(summary.totalValue)} em jogo`);
    }

    const backlog = (summary.pendingTotal || 0) - (summary.count || 0);
    if (backlog > 0) {
      subtitleParts.push(`${backlog} na fila`);
    }

    if (summary.lastRunAt) {
      subtitleParts.push(`atualizado em ${formatShortDate(summary.lastRunAt)}`);
    }
  }

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Ações recomendadas</h3>
          <span className={styles.subtitle}>
            {subtitleParts.length > 0 ? subtitleParts.join(" · ") : "Regras sobre cobrança, evasão, rematrícula e atualização dos dados"}
          </span>
        </div>
        <Button
          icon={UpdateIcon}
          text={running ? "Gerando..." : "Recalcular"}
          size="normal"
          variation="outline"
          loading={running}
          disabled={loading}
          onClick={handleRun}
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {running && (
        <div className={styles.overlay} role="status" aria-live="polite">
          <span className={styles.overlaySpinner} />
          <span>Recalculando ações, desfechos e alertas...</span>
        </div>
      )}

      {outcomes?.measuredActions > 0 && (
        <div className={styles.results}>
          <span className={styles.resultsCaption}>
            {hasResults
              ? `Resultado das ações · abertas ou encerradas nos últimos ${OUTCOME_DAYS} dias`
              : `Resultado das ações · ainda sem pagamento, retenção ou rematrícula medidos nos últimos ${OUTCOME_DAYS} dias`}
          </span>
          {hasResults && (
          <div className={styles.resultsGrid}>
            <div className={styles.resultTile}>
              <span className={styles.resultValue}>{formatNumberToCurrency(Number(outcomes.recoveredValue))}</span>
              <span className={styles.resultLabel}>recebidos em cobrança</span>
              <span className={styles.resultHint}>
                {pluralize(outcomes.paidInstallments, "parcela paga", "parcelas pagas")} enquanto a ação esteve aberta
              </span>
            </div>
            <div className={styles.resultTile}>
              <span className={styles.resultValue}>{outcomes.retainedStudents}</span>
              <span className={styles.resultLabel}>{outcomes.retainedStudents === 1 ? "aluno retido" : "alunos retidos"}</span>
              <span className={styles.resultHint}>
                {outcomes.droppedStudents > 0
                  ? `saíram do risco alto de evasão · ${pluralize(outcomes.droppedStudents, "evadiu", "evadiram")}`
                  : "saíram do risco alto de evasão"}
              </span>
            </div>
            <div className={styles.resultTile}>
              <span className={styles.resultValue}>{outcomes.reenrolledStudents}</span>
              <span className={styles.resultLabel}>{outcomes.reenrolledStudents === 1 ? "rematriculado" : "rematriculados"}</span>
              <span className={styles.resultHint}>
                {outcomes.retainedValue > 0
                  ? `${formatNumberToCurrency(Number(outcomes.retainedValue))} de receita mantida (estimativa)`
                  : "período letivo seguinte confirmado"}
              </span>
            </div>
          </div>
          )}
        </div>
      )}

      {loading ? (
        <p className={styles.empty}>Carregando ações...</p>
      ) : (
        <ul className={styles.list}>
          {items.map((item) => {
            const evidence = parseEvidence(item);
            const progress = outcomeSummary(item);

            return (
              <li key={item.id} className={styles.item}>
                <div className={styles.row}>
                  <span className={`${styles.dot} ${styles[`priority_${item.priority}`] || ""}`} title={`Prioridade ${PRIORITY_LABEL[item.priority] || ""}`} />
                  <div className={styles.info}>
                    <span className={styles.itemTitle}>{item.title}</span>
                    <span className={styles.itemMeta}>
                      {evidenceSummary(item, evidence)}
                      {progress && <span className={styles.progress}> · {progress}</span>}
                    </span>
                  </div>
                  {item.estimatedValue > 0 && (
                    <span className={styles.value}>{formatNumberToCurrency(item.estimatedValue)}</span>
                  )}
                  <div className={styles.actions}>
                    <button type="button" className={styles.linkButton} onClick={() => setSelected({ item, evidence })}>
                      Detalhes
                    </button>
                    <button type="button" className={styles.mutedButton} onClick={() => handleDismiss(item)}>
                      Ignorar
                    </button>
                  </div>
                </div>
              </li>
            );
          })}

          {items.length === 0 && (
            <li className={styles.empty}>
              {summary?.lastRunAt
                ? "Nenhuma ação pendente. Tudo em dia."
                : "As ações ainda não foram geradas para esta instituição. Clique em Recalcular."}
            </li>
          )}
        </ul>
      )}

      {selected && (
        <ActionDetailsModal
          item={selected.item}
          evidence={selected.evidence}
          onClose={() => setSelected(null)}
        />
      )}

      {dismissing && (
        <DismissConfirmModal
          item={dismissing}
          onConfirm={dismissItem}
          onClose={() => setDismissing(null)}
        />
      )}

      <p className={styles.importNote}>
        Mantenha as importações em dia: as ações e os indicadores são recalculados a partir do
        último arquivo importado, então dados atrasados geram recomendações atrasadas.
      </p>
    </section>
  );
};

export default ActionQueue;
